import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, push, set, remove, update } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { encryptEmailPassword, isPasswordEncrypted } from '../../utils/encryption';
import './EmailSenderSettings.css';

const EmailSenderSettings = ({ onClose, onSenderSelected }) => {
    const [senders, setSenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingSender, setEditingSender] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        replyTo: '',
        isDefault: false,
        // SMTP Settings (Hostinger als Standard)
        smtp_host: 'smtp.hostinger.com',
        smtp_port: '465',
        smtp_encryption: 'SSL',
        email_password: '',
        // Optional: IMAP für Antworten (falls gewünscht)
        imap_host: 'imap.hostinger.com',
        imap_port: '993',
        imap_encryption: 'SSL'
    });
    const [saving, setSaving] = useState(false);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Email provider presets (nur Hostinger als Standard)
    const emailProviderPresets = {
        hostinger: {
            smtp_host: 'smtp.hostinger.com',
            smtp_port: '465',
            smtp_encryption: 'SSL',
            imap_host: 'imap.hostinger.com',
            imap_port: '993',
            imap_encryption: 'SSL'
        }
    };

    // Load user's email senders
    useEffect(() => {
        if (currentUser) {
            const sendersRef = ref(db, `emailSenders/${currentUser.uid}`);
            
            onValue(sendersRef, (snapshot) => {
                if (snapshot.exists()) {
                    const sendersData = [];
                    snapshot.forEach((childSnapshot) => {
                        sendersData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setSenders(sendersData.sort((a, b) => b.createdAt - a.createdAt));
                } else {
                    setSenders([]);
                }
                setLoading(false);
            });
        }
    }, [currentUser, db]);

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            
            // Auto-configure encryption based on SMTP port
            if (field === 'smtp_port') {
                if (value === '465') {
                    newData.smtp_encryption = 'SSL';
                } else if (value === '587' || value === '25') {
                    newData.smtp_encryption = 'STARTTLS';
                } else {
                    newData.smtp_encryption = 'STARTTLS'; // Default
                }
            }
            
            // Auto-configure IMAP encryption based on port
            if (field === 'imap_port') {
                if (value === '993') {
                    newData.imap_encryption = 'SSL';
                } else if (value === '143') {
                    newData.imap_encryption = 'STARTTLS';
                } else {
                    newData.imap_encryption = 'SSL'; // Default
                }
            }
            
            return newData;
        });
    };

    const applyProviderPreset = (provider) => {
        const preset = emailProviderPresets[provider];
        if (preset) {
            setFormData(prev => ({
                ...prev,
                ...preset
            }));
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            alert('Bitte geben Sie einen Absender-Namen ein');
            return false;
        }
        if (!formData.email.trim()) {
            alert('Bitte geben Sie eine E-Mail-Adresse ein');
            return false;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Bitte geben Sie eine gültige E-Mail-Adresse ein');
            return false;
        }

        // SMTP validation
        if (!formData.smtp_host.trim()) {
            alert('Bitte geben Sie einen SMTP-Host ein');
            return false;
        }
        if (!formData.smtp_port.trim()) {
            alert('Bitte geben Sie einen SMTP-Port ein');
            return false;
        }
        if (!formData.email_password.trim()) {
            alert('Bitte geben Sie ein E-Mail-Passwort ein');
            return false;
        }

        // Port validation
        const smtpPort = parseInt(formData.smtp_port);
        if (isNaN(smtpPort) || smtpPort < 1 || smtpPort > 65535) {
            alert('Bitte geben Sie einen gültigen SMTP-Port ein (1-65535)');
            return false;
        }

        return true;
    };

    const handleCreateSender = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            // Verschlüssele das Passwort wie in World Settings
            let senderData = { ...formData };
            if (formData.email_password && !isPasswordEncrypted(formData.email_password)) {
                console.log('🔐 Encrypting email password...');
                senderData.email_password = await encryptEmailPassword(formData.email_password);
                console.log('✅ Password encrypted successfully');
            }

            senderData = {
                ...senderData,
                userId: currentUser.uid,
                createdAt: Date.now(),
                updatedAt: Date.now()
            };

            // If this is the first sender or marked as default, make it default
            if (senders.length === 0 || formData.isDefault) {
                // Remove default from other senders
                for (const sender of senders) {
                    if (sender.isDefault) {
                        await update(ref(db, `emailSenders/${currentUser.uid}/${sender.id}`), {
                            isDefault: false
                        });
                    }
                }
                senderData.isDefault = true;
            }

            const newSenderRef = push(ref(db, `emailSenders/${currentUser.uid}`));
            await set(newSenderRef, senderData);

            // Reset form
            setFormData({
                name: '',
                email: '',
                replyTo: '',
                isDefault: false,
                smtp_host: 'smtp.hostinger.com',
                smtp_port: '465',
                smtp_encryption: 'SSL',
                email_password: '',
                imap_host: 'imap.hostinger.com',
                imap_port: '993',
                imap_encryption: 'SSL'
            });
            setShowCreateForm(false);

        } catch (error) {
            console.error('Error creating sender:', error);
            alert('Fehler beim Erstellen des Absenders. Bitte versuchen Sie es erneut.');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateSender = async () => {
        if (!validateForm() || !editingSender) return;

        setSaving(true);
        try {
            // Verschlüssele das Passwort falls nötig
            let senderData = { ...formData };
            if (formData.email_password && !isPasswordEncrypted(formData.email_password)) {
                console.log('🔐 Encrypting email password...');
                senderData.email_password = await encryptEmailPassword(formData.email_password);
                console.log('✅ Password encrypted successfully');
            }

            senderData = {
                ...senderData,
                updatedAt: Date.now()
            };

            // If marked as default, remove default from other senders
            if (formData.isDefault) {
                for (const sender of senders) {
                    if (sender.id !== editingSender.id && sender.isDefault) {
                        await update(ref(db, `emailSenders/${currentUser.uid}/${sender.id}`), {
                            isDefault: false
                        });
                    }
                }
            }

            await update(ref(db, `emailSenders/${currentUser.uid}/${editingSender.id}`), senderData);

            setEditingSender(null);
            setFormData({
                name: '',
                email: '',
                replyTo: '',
                isDefault: false,
                smtp_host: 'smtp.hostinger.com',
                smtp_port: '465',
                smtp_encryption: 'SSL',
                email_password: '',
                imap_host: 'imap.hostinger.com',
                imap_port: '993',
                imap_encryption: 'SSL'
            });

        } catch (error) {
            console.error('Error updating sender:', error);
            alert('Fehler beim Aktualisieren des Absenders. Bitte versuchen Sie es erneut.');
        } finally {
            setSaving(false);
        }
    };

    const handleEditSender = (sender) => {
        setEditingSender(sender);
        setFormData({
            name: sender.name,
            email: sender.email,
            replyTo: sender.replyTo || '',
            isDefault: sender.isDefault || false,
            smtp_host: sender.smtp_host || 'smtp.hostinger.com',
            smtp_port: sender.smtp_port || '465',
            smtp_encryption: sender.smtp_encryption || 'SSL',
            email_password: sender.email_password || '',
            imap_host: sender.imap_host || 'imap.hostinger.com',
            imap_port: sender.imap_port || '993',
            imap_encryption: sender.imap_encryption || 'SSL'
        });
        setShowCreateForm(true);
    };

    const handleDeleteSender = async (senderId) => {
        if (!window.confirm('Sind Sie sicher, dass Sie diesen Absender löschen möchten?')) {
            return;
        }

        try {
            await remove(ref(db, `emailSenders/${currentUser.uid}/${senderId}`));
        } catch (error) {
            console.error('Error deleting sender:', error);
            alert('Fehler beim Löschen des Absenders. Bitte versuchen Sie es erneut.');
        }
    };

    const handleSetDefault = async (senderId) => {
        try {
            // Remove default from all senders
            for (const sender of senders) {
                await update(ref(db, `emailSenders/${currentUser.uid}/${sender.id}`), {
                    isDefault: sender.id === senderId
                });
            }
        } catch (error) {
            console.error('Error setting default sender:', error);
            alert('Fehler beim Setzen des Standard-Absenders. Bitte versuchen Sie es erneut.');
        }
    };

    const cancelForm = () => {
        setShowCreateForm(false);
        setEditingSender(null);
        setFormData({
            name: '',
            email: '',
            replyTo: '',
            isDefault: false,
            smtp_host: 'smtp.hostinger.com',
            smtp_port: '465',
            smtp_encryption: 'SSL',
            email_password: '',
            imap_host: 'imap.hostinger.com',
            imap_port: '993',
            imap_encryption: 'SSL'
        });
    };

    if (loading) {
        return (
            <div className="email-sender-settings-modal">
                <div className="email-sender-settings-content">
                    <div className="email-sender-loading">
                        <div className="spinner"></div>
                        <p>Loading senders...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="email-sender-settings-modal">
            <div className="email-sender-settings-content">
                <div className="email-sender-header">
                    <h3>📧 Email Sender Settings</h3>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                {!showCreateForm ? (
                    <div className="email-sender-list">
                        <div className="sender-list-header">
                            <h4>Your Email Senders</h4>
                            <button 
                                className="create-sender-btn"
                                onClick={() => setShowCreateForm(true)}
                            >
                                ➕ Add Sender
                            </button>
                        </div>

                        {senders.length === 0 ? (
                            <div className="empty-senders">
                                <div className="empty-icon">📧</div>
                                <h5>No Email Senders</h5>
                                <p>Create your first email sender to start sending templates</p>
                                <button 
                                    className="create-first-sender-btn"
                                    onClick={() => setShowCreateForm(true)}
                                >
                                    Create First Sender
                                </button>
                            </div>
                        ) : (
                            <div className="senders-grid">
                                {senders.map(sender => (
                                    <div key={sender.id} className={`sender-card ${sender.isDefault ? 'default' : ''}`}>
                                        <div className="sender-card-header">
                                            <div className="sender-info">
                                                <h5>{sender.name}</h5>
                                                <p className="sender-email">{sender.email}</p>
                                                {sender.replyTo && (
                                                    <p className="sender-reply">Reply: {sender.replyTo}</p>
                                                )}
                                            </div>
                                            {sender.isDefault && (
                                                <div className="default-badge">
                                                    ⭐ Default
                                                </div>
                                            )}
                                        </div>

                                        <div className="sender-actions">
                                            {!sender.isDefault && (
                                                <button
                                                    className="set-default-btn"
                                                    onClick={() => handleSetDefault(sender.id)}
                                                >
                                                    ⭐ Set Default
                                                </button>
                                            )}
                                            <button
                                                className="edit-sender-btn"
                                                onClick={() => handleEditSender(sender)}
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                className="delete-sender-btn"
                                                onClick={() => handleDeleteSender(sender.id)}
                                            >
                                                🗑️ Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {senders.length > 0 && onSenderSelected && (
                            <div className="sender-selection-actions">
                                <button 
                                    className="continue-btn"
                                    onClick={() => {
                                        const defaultSender = senders.find(s => s.isDefault) || senders[0];
                                        onSenderSelected(defaultSender);
                                    }}
                                >
                                    Continue with Default Sender
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="email-sender-form">
                        <h4>{editingSender ? 'Edit Sender' : 'Create New Sender'}</h4>
                        
                        <div className="form-group">
                            <label>Sender Name *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="e.g., BonusClub Support"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email Address *</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="e.g., support@bonusclub.com"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email Password *</label>
                            <input
                                type="password"
                                value={formData.email_password}
                                onChange={(e) => handleInputChange('email_password', e.target.value)}
                                placeholder="Your email password or app password"
                            />
                        </div>

                        <div className="form-group">
                            <label>Reply-To Address (optional)</label>
                            <input
                                type="email"
                                value={formData.replyTo}
                                onChange={(e) => handleInputChange('replyTo', e.target.value)}
                                placeholder="e.g., noreply@bonusclub.com"
                            />
                        </div>

                        <div className="form-section-header">
                            <h5>📤 SMTP Configuration</h5>
                            <p>Configure your email server settings for sending emails</p>
                        </div>

                        <div className="provider-presets">
                            <label>Standard Configuration:</label>
                            <div className="preset-buttons">
                                <button type="button" onClick={() => applyProviderPreset('hostinger')}>
                                    📧 Use Hostinger Settings
                                </button>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>SMTP Host *</label>
                                <input
                                    type="text"
                                    value={formData.smtp_host}
                                    onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                                    placeholder="e.g., smtp.hostinger.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>SMTP Port *</label>
                                <input
                                    type="text"
                                    value={formData.smtp_port}
                                    onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                                    placeholder="e.g., 465"
                                />
                            </div>
                        </div>

                        <div className="encryption-info">
                            <p>🔒 Encryption is automatically configured based on your SMTP settings</p>
                        </div>

                        <div className="form-section-header">
                            <h5>📥 IMAP Configuration (Optional)</h5>
                            <p>For reading email replies (optional feature)</p>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>IMAP Host</label>
                                <input
                                    type="text"
                                    value={formData.imap_host}
                                    onChange={(e) => handleInputChange('imap_host', e.target.value)}
                                    placeholder="e.g., imap.hostinger.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>IMAP Port</label>
                                <input
                                    type="text"
                                    value={formData.imap_port}
                                    onChange={(e) => handleInputChange('imap_port', e.target.value)}
                                    placeholder="e.g., 993"
                                />
                            </div>
                        </div>

                        <div className="encryption-info">
                            <p>🔒 IMAP encryption is also automatically configured (SSL for port 993, STARTTLS for port 143)</p>
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.isDefault}
                                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                                />
                                Set as default sender
                            </label>
                        </div>

                        <div className="form-actions">
                            <button 
                                className="cancel-btn"
                                onClick={cancelForm}
                            >
                                Cancel
                            </button>
                            <button 
                                className="save-sender-btn"
                                onClick={editingSender ? handleUpdateSender : handleCreateSender}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : editingSender ? 'Update Sender' : 'Create Sender'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailSenderSettings;
